import { AppInterfaceConfig, AppThemePalette, ThemeApiStructure } from "@interfaces/theme";

/**
 * Colores de marca de Lendia: respaldo del loader mientras llega la config y de cualquier llave de
 * `primary` que le falte al registro de la base.
 */
export const DEFAULT_PRIMARY: AppThemePalette["primary"] = {
  main: "#528CD6",
  dark: "#132B52",
  light: "#A3D4E8",
  base: "#E3F2F8",
  accent: "#F2704F",
};

/** Llaves de `primary` que toda marca debe traer; `hover` es opcional. */
const REQUIRED_PRIMARY_KEYS = ["main", "dark", "light", "base", "accent"] as const;

export const DEFAULT_IMAGE_LIST: string[] = [
  "lendia/portal/lateral/imagen_1.webp",
  "lendia/portal/lateral/imagen_2.webp",
  "lendia/portal/lateral/imagen_3.webp",
  "lendia/portal/lateral/imagen_4.webp",
  "lendia/portal/lateral/imagen_5.webp",
  "lendia/portal/lateral/imagen_6.webp",
  "lendia/portal/lateral/imagen_7.webp",
  "lendia/portal/lateral/imagen_8.webp",
  "lendia/portal/lateral/imagen_9.webp",
  "lendia/portal/lateral/imagen_10.webp",
];

export const DEFAULT_TEXT_LIST: string[] = [
  "Así de fácil: solicita, compara, avanza",
  "Tu crédito, tu elección, tu crecimiento",
  "Crédito simple, procesos simples",
  "Avanza con las mejores condiciones del mercado",
  "Compara y decide: tu crédito, tus reglas",
  "Un solo lugar, todas las opciones de financiamiento",
  "Crecer nunca fue tan sencillo",
  "Descubre la mejor solución para tu PyME",
  "El poder de elegir está en tus manos",
  "Da el paso que tu empresa necesita",
];

/**
 * Medida de un logo tal como viene de la base: varios registros la guardan como texto (`"150"`).
 * Un número en texto se convierte; cualquier otro texto (`"auto"`) se respeta.
 */
export const normalizeLogoSize = (value: number | string | undefined): number | string | undefined => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed !== "" && !Number.isNaN(Number(trimmed)) ? Number(trimmed) : value;
};

/** Una lista vacía cuenta como "no definida": se usan las de Lendia en vez de dejar el lateral vacío. */
const listOrDefault = (list: string[] | undefined, fallback: string[]) => (list && list.length > 0 ? list : fallback);

/**
 * Paleta de la marca lista para el theme: completa con Lendia las llaves de `primary` que falten y
 * regresa cuáles faltaban, para reportarlas. `components` siempre existe (vacío si no viene).
 */
export const normalizePalette = (
  palette: Partial<AppThemePalette> | undefined,
): { palette: AppThemePalette; missingPrimary: string[] } => {
  const primary = palette?.primary;
  const missingPrimary = REQUIRED_PRIMARY_KEYS.filter((key) => !primary?.[key]);

  return {
    palette: {
      ...palette,
      primary: { ...DEFAULT_PRIMARY, ...primary },
      components: palette?.components ?? {},
    },
    missingPrimary,
  };
};

/**
 * Interfaz de la marca lista para usar: URLs con su respaldo, medidas de logo como número y las
 * listas del lateral con las de Lendia cuando vienen vacías.
 */
export const normalizeInterface = (response: ThemeApiStructure): AppInterfaceConfig => {
  const { interface: config } = response.palette;
  const { lateral } = config;

  return {
    ...config,
    url_home: config.url_home || "/solicita_tu_credito",
    url_privacy_notice: response.url_privacy_notice || "/aviso_de_privacidad",
    url_terms_and_conditions: response.url_terms_and_conditions || "/terminos_y_condiciones",
    navbar: {
      ...config.navbar,
      logo_width: normalizeLogoSize(config.navbar.logo_width) as number | undefined,
      logo_height: normalizeLogoSize(config.navbar.logo_height),
    },
    lateral: lateral
      ? {
          ...lateral,
          image_list: listOrDefault(lateral.image_list, DEFAULT_IMAGE_LIST),
          text_list: listOrDefault(lateral.text_list, DEFAULT_TEXT_LIST),
          logo_width: normalizeLogoSize(lateral.logo_width) as number | undefined,
          logo_height: normalizeLogoSize(lateral.logo_height),
        }
      : undefined,
  };
};
